import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ExternalLink, ShieldAlert, Lock } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import businessPlanAsset from "@/assets/cerveza-business-plan.pdf.asset.json";
import { PdfAllPages } from "@/components/admin/ManualPdfThumb";

export const Route = createFileRoute("/majitele-vacantu")({
  ssr: false,
  component: MajiteleVacantuPage,
  head: () => ({
    meta: [
      { title: "Majitelé Vacantů — Cerveza.International" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function MajiteleVacantuPage() {
  const { user, hasRole, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const qc = useQueryClient();

  const isVakantOwner = hasRole("vakant_owner");
  const isAdmin = hasRole("admin");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: "/auth", search: { redirect: "/majitele-vacantu" } });
      return;
    }
    if (!isVakantOwner && !isAdmin) {
      navigate({ to: "/" });
    }
  }, [loading, user, isVakantOwner, isAdmin, navigate]);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["my-profile-vakanty", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name,email,vakanty_purchased,nda_business_plan_accepted_at")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: features = [] } = useQuery({
    queryKey: ["vakant-owner-features"],
    enabled: !!user && (isVakantOwner || isAdmin),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vakant_owner_features")
        .select("id,title,description,url,kind,sort_order")
        .eq("active", true)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const [ndaChecked, setNdaChecked] = useState(false);
  const [showPlan, setShowPlan] = useState(false);

  const acceptMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("profiles")
        .update({ nda_business_plan_accepted_at: new Date().toISOString() })
        .eq("id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("vakanty.bp.acceptedToast"));
      qc.invalidateQueries({ queryKey: ["my-profile-vakanty", user?.id] });
      setShowPlan(true);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading || !user) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        {t("vakanty.loading")}
      </div>
    );
  }

  const count = profile?.vakanty_purchased ?? 0;
  const alreadyAccepted = !!profile?.nda_business_plan_accepted_at;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <div className="mb-8">
        <h1 className="font-serif text-3xl">{t("vakanty.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {profile?.full_name
            ? t("vakanty.welcomeWithName", { name: profile.full_name })
            : t("vakanty.welcomeNoName")}
        </p>
      </div>

      <Card>
        <CardContent className="p-8 text-center">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("vakanty.loading")}
            </div>
          ) : (
            <>
              <div className="text-sm uppercase tracking-wide text-muted-foreground">
                {t("vakanty.purchasedLabel")}
              </div>
              <div className="mt-2 font-serif text-6xl font-semibold text-primary">
                {count}
              </div>
              {count === 0 && (
                <p className="mt-4 text-sm text-muted-foreground">
                  {t("vakanty.emptyHint")}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Confidential Business Plan */}
      <Card className="mt-6 border-primary/30">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-6 w-6 shrink-0 text-primary" />
            <div>
              <h2 className="font-serif text-2xl">{t("vakanty.bp.title")}</h2>
              <p className="text-sm text-muted-foreground">{t("vakanty.bp.subtitle")}</p>
            </div>
          </div>

          <div className="rounded-md border border-primary/40 bg-primary/5 p-4 text-sm leading-relaxed">
            <p className="font-medium text-primary">{t("vakanty.bp.strictHeader")}</p>
            <p className="mt-2 text-foreground/90">{t("vakanty.bp.paragraph1")}</p>
            <p className="mt-2 text-foreground/90">{t("vakanty.bp.paragraph2")}</p>
          </div>

          {alreadyAccepted && (
            <div className="text-xs text-muted-foreground">
              {t("vakanty.bp.acceptedOn", {
                date: new Date(profile!.nda_business_plan_accepted_at as string).toLocaleString(),
              })}
            </div>
          )}

          {!alreadyAccepted && (
            <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/30">
              <Checkbox
                checked={ndaChecked}
                onCheckedChange={(v) => setNdaChecked(v === true)}
                className="mt-0.5"
              />
              <span className="text-sm">{t("vakanty.bp.checkboxLabel")}</span>
            </label>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {!showPlan ? (
              <Button
                onClick={() => {
                  if (alreadyAccepted) {
                    setShowPlan(true);
                    return;
                  }
                  if (!ndaChecked) {
                    toast.error(t("vakanty.bp.mustCheckFirst"));
                    return;
                  }
                  acceptMut.mutate();
                }}
                disabled={acceptMut.isPending || (!alreadyAccepted && !ndaChecked)}
              >
                <Lock className="mr-2 h-4 w-4" />
                {alreadyAccepted ? t("vakanty.bp.showButton") : t("vakanty.bp.acceptButton")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setShowPlan(false)}>
                {t("vakanty.bp.hideButton")}
              </Button>
            )}
          </div>

          {showPlan && (
            <div
              className="mt-4 rounded-md border bg-muted/30"
              onContextMenu={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{ userSelect: "none" }}
            >
              <div className="border-b bg-background/80 px-3 py-2 text-xs text-muted-foreground">
                {t("vakanty.bp.viewerNotice")}
              </div>
              <div className="h-[80vh]">
                <PdfAllPages url={businessPlanAsset.url} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {features.length > 0 && (
        <div className="mt-10 space-y-3">
          <h2 className="font-serif text-2xl">{t("vakanty.featuresTitle")}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {features.map((f) => (
              <Card key={f.id}>
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-medium">{f.title}</h3>
                    <Badge variant={f.kind === "feature" ? "default" : "secondary"}>
                      {f.kind === "feature" ? t("vakanty.kindFeature") : t("vakanty.kindInfo")}
                    </Badge>
                  </div>
                  {f.description && (
                    <p className="text-sm text-muted-foreground whitespace-pre-line">
                      {f.description}
                    </p>
                  )}
                  {f.url && (
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      {t("vakanty.open")} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
